import type { CanvasBin } from '@/types'

/**
 * Reduce a hierarchy-aware group move to its independent roots.
 *
 * A selected container already carries every descendant visually and in the
 * persisted hierarchy. Writing a second move for one of those descendants
 * would detach it from the selected bin at drop time. Preserve the caller's
 * order for peer roots, remove duplicates, and cap corrupt parent walks.
 */
export function pruneRedundantCanvasMoveIds(
  nodeIds: Iterable<string>,
  getParentId: (nodeId: string) => string | undefined,
): string[] {
  const ordered = Array.from(new Set(nodeIds))
  const selected = new Set(ordered)

  return ordered.filter(nodeId => {
    const seen = new Set<string>([nodeId])
    let parentId = getParentId(nodeId)
    while (parentId && !seen.has(parentId)) {
      if (selected.has(parentId)) return false
      seen.add(parentId)
      parentId = getParentId(parentId)
    }
    return true
  })
}

/**
 * Validate a proposed canvas-bin parent without trusting the caller.
 * Walking upward from the target catches both direct self-parenting and
 * moving a bin under one of its descendants.
 */
export function isValidCanvasBinParent(
  binId: string,
  parentBinId: string | undefined,
  bins: { get(id: string): CanvasBin | undefined },
): boolean {
  if (!parentBinId) return true
  if (parentBinId === binId) return false

  const source = bins.get(binId)
  const target = bins.get(parentBinId)
  if (!source || !target || source.sceneId !== target.sceneId) return false

  const seen = new Set<string>()
  let currentId: string | undefined = parentBinId
  while (currentId) {
    if (currentId === binId || seen.has(currentId)) return false
    seen.add(currentId)
    currentId = bins.get(currentId)?.parentBinId
  }
  return true
}
